import type { Color } from '@blocksuite/affine-model';
import type { GfxCompatibleProps } from '@blocksuite/std/gfx';
import { GfxCompatible } from '@blocksuite/std/gfx';
import {
  BlockModel,
  BlockSchemaExtension,
  defineBlockSchema,
} from '@blocksuite/store';

export const STICKY_NOTE_COLORS = ['yellow', 'pink', 'blue', 'green'] as const;

export type StickyNoteColor = (typeof STICKY_NOTE_COLORS)[number];

export type StickyNoteBlockProps = {
  text: string;
  color: StickyNoteColor;
  textColor: Color;
  fontSize: number;
  /** Stable workspace member ID. Empty means unassigned. */
  assigneeId: string;
  /** Small persisted canvas rotation in degrees. */
  rotate: number;
  /** Persisted paper curl/outline variant. */
  paperVariant: number;
  /** Horizontal pin displacement from center, normalized to note width. */
  pinOffset: number;
  /** Vertical pin displacement inside the shallow top attachment area. */
  pinOffsetY: number;
  /** Small persisted pin rotation in degrees. */
  pinRotation: number;
} & GfxCompatibleProps;

export class StickyNoteBlockModel extends GfxCompatible<StickyNoteBlockProps>(
  BlockModel
) {}

export const StickyNoteBlockSchema = defineBlockSchema({
  flavour: 'affine:sticky-note',
  props: (): StickyNoteBlockProps => ({
    text: '',
    color: 'yellow',
    textColor: '#312d27',
    fontSize: 21,
    assigneeId: '',
    rotate: 0,
    paperVariant: 0,
    pinOffset: 0,
    pinOffsetY: 0,
    pinRotation: 0,
    xywh: '[0,0,260,210]',
    index: 'a0',
    lockedBySelf: false,
  }),
  metadata: {
    role: 'content',
    version: 1,
    parent: ['@root', 'affine:surface'],
    children: [],
  },
  toModel: () => new StickyNoteBlockModel(),
});

export const StickyNoteBlockSchemaExtension = BlockSchemaExtension(
  StickyNoteBlockSchema
);
